import { Typography, Paper, Stack } from '@mui/material'

import {
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_SHADOW_CARD,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import ClientHeaderCard from './ClientHeaderCard'
import { formatDateTimeFull } from '../../helpers/formatDate'
import { safeValue } from '../../poc/PurchaseOrder/helpers/tableUtils'

const DETAIL_HEADER_CARD_BASE_SX = {
  borderRadius: '10px',
  backgroundColor: UI_COLOR_WHITE,
  boxShadow: UI_COLOR_SHADOW_CARD
}

/**
 * Cómo reutilizar este componente:
 *
 * <ClientHeader
 *   data={refund}
 *   config={{
 *     titleKey: 'client_name',
 *     cards: [
 *       { icon: 'barcode_scanner', label: 'Código:', valueKey: 'client_db_ref' },
 *       { icon: 'person', label: 'Vendedor:', valueKey: 'salesman_name', filled: true },
 *       { icon: 'event', label: 'Fecha:', valueKey: 'created_at', format: 'date' }
 *     ]
 *   }}
 * />
 *
 * Props:
 * - data: objeto con los valores que se van a mostrar.
 * - config.titleKey: nombre de la propiedad de `data` que se usa como título principal.
 * - config.cards: lista de datos compactos que aparecen debajo del título.
 * - card.icon: nombre del GoogleIcon que se va a pintar.
 * - card.label: texto fijo antes del valor.
 * - card.valueKey: nombre de la propiedad de `data` que se va a mostrar.
 * - card.format: usa "date" cuando el valor debe formatearse como fecha/hora.
 * - card.filled / card.iconColor: opciones visuales para el icono.
 */
export default function ClientHeader({
  data,
  config,
  cardSx = {},
  titleSx = {},
  stackSx = {},
  itemSx = {},
  itemIconSx = {},
  itemIconSize,
  itemLabelSx = {},
  itemValueSx = {}
}) {
  if (!data || !config) return null

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2.5,
        mb: 2,
        ...DETAIL_HEADER_CARD_BASE_SX,
        ...cardSx
      }}
    >
      <Typography
        fontWeight={700}
        fontSize={20}
        sx={{ mb: 1, color: UI_COLOR_PRIMARY_ALT, ...titleSx }}
      >
        {safeValue(data[config.titleKey])}
      </Typography>

      <Stack
        direction="row"
        spacing={3}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={stackSx}
      >
        {config.cards.map((card) => {
          const rawValue = data[card.valueKey]

          const value =
            card.format === 'date'
              ? rawValue
                ? formatDateTimeFull(rawValue)
                : '-'
              : safeValue(rawValue)

          return (
            <ClientHeaderCard
              key={card.valueKey}
              icon={card.icon}
              iconColor={card.iconColor}
              filled={card.filled}
              label={card.label}
              value={value}
              sx={itemSx}
              iconSx={itemIconSx}
              iconSize={itemIconSize}
              labelSx={itemLabelSx}
              valueSx={itemValueSx}
            />
          )
        })}
      </Stack>
    </Paper>
  )
}
